import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { useAuth } from './AuthContext.jsx';

const AppContext = createContext({
  interviewee: null,
  loading: false,
  refreshInterviewee: async () => {},
});

export function AppProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [interviewee, setInterviewee] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInterviewee = useCallback(async (userId) => {
    if (!userId) {
      setInterviewee(null);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('interviewees')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('AppContext fetchInterviewee error', error);
      }
      setInterviewee(data ?? null);
    } catch (error) {
      console.error('AppContext fetchInterviewee threw', error);
      setInterviewee(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    fetchInterviewee(user?.id ?? null);
  }, [authLoading, user?.id, fetchInterviewee]);

  const value = useMemo(
    () => ({
      interviewee,
      loading: authLoading || loading,
      refreshInterviewee: () => fetchInterviewee(user?.id ?? null),
    }),
    [interviewee, loading, authLoading, fetchInterviewee, user?.id]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  return useContext(AppContext);
}
